import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Printer, Save, Tag, Eye, EyeOff, Keyboard, ZoomIn, ZoomOut, BookOpen, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  buildFromFields, SALE_FIELD_DEFS, REPAIR_FIELD_DEFS,
  readLabelCfg, sendToQZ, fmtPrice,
  REPAIR_DEFAULTS_KEY, SALE_DEFAULTS_KEY,
} from './labelUtils';

// ── Texte affiché par champ selon les données initiales ──────────────────────
function getFieldText(type, key, data) {
  const today   = new Date();
  const dateStr = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${String(today.getFullYear()).slice(-2)}`;
  const brand = data.brand || '', model = data.model || '';
  if (type === 'sale') {
    switch (key) {
      case 'name':      return [brand, model].filter(Boolean).join(' ') || 'Produit';
      case 'storage':   return data.storage      || '';
      case 'condition': return data.saleCondition ? `État: ${data.saleCondition}` : '';
      case 'battery':   return data.batteryPct   ? `Batterie : ${data.batteryPct}%` : '';
      case 'price':     return data.priceTTC     ? `${fmtPrice(data.priceTTC)}€` : '';
      case 'origin':    return data.originText   || "Produit d'origine";
      case 'payment':   return data.paymentText  || '';
      default: return '';
    }
  } else {
    switch (key) {
      case 'repairNum':   return data.repairNum  ? `#${data.repairNum}` : '';
      case 'date':        return dateStr;
      case 'clientName':  return data.clientName  || '';
      case 'clientPhone': return data.clientPhone || '';
      case 'device':      return [brand, model, data.notes || data.part].filter(Boolean).join(' · ');
      case 'price':       return data.priceTTC ? `${fmtPrice(data.priceTTC)}€` : '';
      case 'pin':         return data.pinCode  || '';
      default: return '';
    }
  }
}

function initFields(type, initial) {
  const savedKey = `phonelab_${type}_field_positions`;
  let saved = {}; try { saved = JSON.parse(localStorage.getItem(savedKey)) || {}; } catch {}
  const defKey = type === 'sale' ? SALE_DEFAULTS_KEY : REPAIR_DEFAULTS_KEY;
  let defaults = {}; try { defaults = JSON.parse(localStorage.getItem(defKey)) || {}; } catch {}
  const defs = type === 'sale' ? SALE_FIELD_DEFS : REPAIR_FIELD_DEFS;
  const data = { ...defaults, ...initial };
  return defs.map(def => {
    const s = saved[def.key] || {};
    return {
      ...def,
      x:              s.x              != null ? s.x              : def.x,
      y:              s.y              != null ? s.y              : def.y,
      fontSize:       s.fontSize       != null ? s.fontSize       : def.fontSize,
      fontWeight:     s.fontWeight     != null ? s.fontWeight     : def.fontWeight,
      textDecoration: s.textDecoration != null ? s.textDecoration : 'none',
      show:           s.show           != null ? s.show           : true,
      text:           getFieldText(type, def.key, data),
    };
  });
}

// ── Bibliothèque de modèles d'étiquettes (localStorage) ─────────────────────
const LABEL_TEMPLATES_KEY = 'phonelab_label_templates';

function loadTemplates(type) {
  try {
    const all = JSON.parse(localStorage.getItem(LABEL_TEMPLATES_KEY)) || [];
    return type ? all.filter(t => t.type === type) : all;
  } catch { return []; }
}

function persistTemplate(name, type, fields, labelWidth, labelHeight) {
  try {
    const all = JSON.parse(localStorage.getItem(LABEL_TEMPLATES_KEY)) || [];
    const tpl = {
      id: Date.now().toString(),
      name: name || 'Modèle sans titre',
      type,
      date: new Date().toISOString(),
      fields: fields.map(f => ({ ...f })),
      labelWidth,
      labelHeight,
    };
    // Remplace si même nom+type, sinon ajoute (max 50)
    const updated = [tpl, ...all.filter(t => !(t.type === type && t.name === name))].slice(0, 50);
    localStorage.setItem(LABEL_TEMPLATES_KEY, JSON.stringify(updated));
    return tpl;
  } catch { return null; }
}

function removeTemplate(id) {
  try {
    const all = JSON.parse(localStorage.getItem(LABEL_TEMPLATES_KEY)) || [];
    localStorage.setItem(LABEL_TEMPLATES_KEY, JSON.stringify(all.filter(t => t.id !== id)));
  } catch {}
}

function renameTemplateById(id, name) {
  try {
    const all = JSON.parse(localStorage.getItem(LABEL_TEMPLATES_KEY)) || [];
    localStorage.setItem(LABEL_TEMPLATES_KEY, JSON.stringify(all.map(t => t.id === id ? { ...t, name } : t)));
  } catch {}
}

// ── Formats prédéfinis ───────────────────────────────────────────────────────
const SIZE_PRESETS = [
  { label: '40 × 30 mm',  w: 40,  h: 30  },
  { label: '50 × 30 mm',  w: 50,  h: 30  },
  { label: '50 × 40 mm',  w: 50,  h: 40  },
  { label: '58 × 40 mm',  w: 58,  h: 40  },
  { label: '62 × 29 mm',  w: 62,  h: 29  },
  { label: '100 × 75 mm', w: 100, h: 75  },
];

// ── Règle horizontale ────────────────────────────────────────────────────────
function RulerH({ width, labelWidth, offsetLeft }) {
  const step = labelWidth <= 50 ? 5 : 10;
  const marks = [];
  for (let i = 0; i <= labelWidth; i += step) {
    marks.push({ x: (i / labelWidth) * width, label: i, major: i % 10 === 0 });
  }
  return (
    <div style={{ width: width + offsetLeft, height: 14, position: 'relative', background: '#111827', borderBottom: '1px solid #1f2937', flexShrink: 0 }}>
      {marks.map(m => (
        <div key={m.label} style={{ position: 'absolute', left: m.x + offsetLeft, top: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {m.major && m.label > 0 && <span style={{ fontSize: 6, color: '#6b7280', lineHeight: 1, position: 'absolute', top: 0, transform: 'translateX(-50%)' }}>{m.label}</span>}
          <div style={{ width: 1, height: m.major ? 7 : 4, background: m.major ? '#4b5563' : '#374151', position: 'absolute', bottom: 0 }} />
        </div>
      ))}
    </div>
  );
}

// ── Règle verticale ──────────────────────────────────────────────────────────
function RulerV({ height, labelHeight }) {
  const step = labelHeight <= 30 ? 5 : 10;
  const marks = [];
  for (let i = 0; i <= labelHeight; i += step) {
    marks.push({ y: (i / labelHeight) * height, label: i, major: i % 10 === 0 });
  }
  return (
    <div style={{ width: 18, height, position: 'relative', background: '#111827', borderRight: '1px solid #1f2937', flexShrink: 0 }}>
      {marks.map(m => (
        <div key={m.label} style={{ position: 'absolute', top: m.y, right: 0, display: 'flex', alignItems: 'center', width: '100%' }}>
          {m.major && m.label > 0 && (
            <span style={{ fontSize: 6, color: '#6b7280', lineHeight: 1, position: 'absolute', left: 1, transform: 'translateY(-50%)' }}>{m.label}</span>
          )}
          <div style={{ width: m.major ? 7 : 4, height: 1, background: m.major ? '#4b5563' : '#374151', position: 'absolute', right: 0 }} />
        </div>
      ))}
    </div>
  );
}

// ── Barre d'outils canvas ────────────────────────────────────────────────────
function CanvasToolbar({ field, onEdit, onFontSize, onToggleVis, onBold, onUnderline }) {
  if (!field) return null;
  const isBold      = (field.fontWeight || 400) >= 700;
  const isUnderline = field.textDecoration === 'underline';
  return (
    <div className="flex items-center gap-0.5 bg-gray-800 border border-gray-600 rounded-xl px-2 py-1 shadow-xl mt-1">
      <button onClick={onEdit} title="Éditer le texte (ou double-clic)"
        className="p-1.5 text-gray-300 hover:text-white rounded-lg hover:bg-gray-700 transition-colors">
        <Keyboard size={13} />
      </button>
      <div className="w-px h-4 bg-gray-600 mx-0.5" />
      <button onClick={onBold} title={isBold ? 'Normal' : 'Gras'}
        className={`px-2 py-1 rounded-lg hover:bg-gray-700 transition-colors text-[13px] font-black leading-none ${isBold ? 'text-blue-300 bg-gray-700' : 'text-gray-400'}`}>
        B
      </button>
      <button onClick={onUnderline} title={isUnderline ? 'Supprimer soulignement' : 'Souligner'}
        className={`px-2 py-1 rounded-lg hover:bg-gray-700 transition-colors text-[13px] underline leading-none ${isUnderline ? 'text-blue-300 bg-gray-700' : 'text-gray-400'}`}>
        U
      </button>
      <div className="w-px h-4 bg-gray-600 mx-0.5" />
      <button onClick={() => onFontSize(+1)} title="Augmenter la taille"
        className="p-1.5 text-gray-300 hover:text-white rounded-lg hover:bg-gray-700 transition-colors">
        <ZoomIn size={13} />
      </button>
      <span className="text-white text-[11px] font-mono w-6 text-center select-none">{field.fontSize}</span>
      <button onClick={() => onFontSize(-1)} title="Réduire la taille"
        className="p-1.5 text-gray-300 hover:text-white rounded-lg hover:bg-gray-700 transition-colors">
        <ZoomOut size={13} />
      </button>
      <div className="w-px h-4 bg-gray-600 mx-0.5" />
      <button onClick={onToggleVis} title={field.show ? 'Masquer' : 'Afficher'}
        className={`p-1.5 rounded-lg hover:bg-gray-700 transition-colors ${field.show ? 'text-blue-400' : 'text-gray-600'}`}>
        {field.show ? <Eye size={13} /> : <EyeOff size={13} />}
      </button>
    </div>
  );
}

// ── Éditeur inline isolé ─────────────────────────────────────────────────────
// Utilise useEffect(()=>{},  []) pour définir le texte UNE SEULE FOIS au montage.
// N'utilise PAS dangerouslySetInnerHTML (ce qui causait l'effacement automatique
// du texte à chaque re-render de React).
function InlineEditor({ text, style, onCommit }) {
  const ref = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerText = text || '';
    el.focus();
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      if (sel) { sel.removeAllRanges(); sel.addRange(range); }
    } catch {}
  }, []); // ← IMPORTANT : dépendances vides — exécuté une seule fois au montage
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={e => onCommit(e.target.innerText.replace(/\n/g, '').trim())}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault();
          onCommit(e.target.innerText.replace(/\n/g, '').trim());
        }
      }}
      style={style}
    />
  );
}

// ── Canvas drag-and-drop + édition inline ────────────────────────────────────
function DragLabelCanvas({ fields, labelWidth, labelHeight, selected, onSelect, onMove, onUpdate }) {
  const pxW   = Math.round(labelWidth  * 96 / 25.4);
  const pxH   = Math.round(labelHeight * 96 / 25.4);
  const SCALE = Math.min(3.6, 320 / pxW);
  const W     = Math.round(pxW * SCALE);
  const H     = Math.round(pxH * SCALE);
  const RULER = 18;

  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [editing,  setEditing]  = useState(null);

  // Refs anti-stale-closure
  const WRef = useRef(W), HRef = useRef(H), onMoveRef = useRef(onMove), onUpdateRef = useRef(onUpdate);
  useEffect(() => { WRef.current = W; HRef.current = H; onMoveRef.current = onMove; onUpdateRef.current = onUpdate; });

  // ── Drag ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const nx = Math.max(0, Math.min(96, dragging.origX + (cx - dragging.startX) / WRef.current * 100));
      const ny = Math.max(0, Math.min(92, dragging.origY + (cy - dragging.startY) / HRef.current * 100));
      onMoveRef.current(dragging.key, nx, ny);
    };
    const up = () => setDragging(null);
    window.addEventListener('mousemove', move);   window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false }); window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up);
    };
  }, [dragging]);

  // ── Resize (font size via poignée ↔) ────────────────────────────────────
  useEffect(() => {
    if (!resizing) return;
    const move = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const dx = cx - resizing.startX;
      const ns = Math.max(5, Math.min(30, Math.round(resizing.startFs + dx / 4)));
      onUpdateRef.current(resizing.key, { fontSize: ns });
    };
    const up = () => setResizing(null);
    window.addEventListener('mousemove', move);  window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false }); window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up);
    };
  }, [resizing]);

  const startDrag = (key, cx, cy, e) => {
    if (editing === key) return;
    e.stopPropagation(); e.preventDefault();
    onSelect(key);
    const f = fields.find(f => f.key === key);
    if (f) setDragging({ key, startX: cx, startY: cy, origX: f.x, origY: f.y });
  };

  const startEdit = (key, e) => {
    e.stopPropagation();
    setEditing(key);
    onSelect(key);
  };

  const commitEdit = useCallback((key, text) => {
    onUpdateRef.current(key, { text });
    setEditing(null);
  }, []);

  const selectedField = fields.find(f => f.key === selected);

  return (
    <div className="flex flex-col items-center">
      <RulerH width={W} labelWidth={labelWidth} offsetLeft={RULER} />

      <div className="flex">
        <RulerV height={H} labelHeight={labelHeight} />

        {/* Canvas étiquette */}
        <div
          style={{ width: W, height: H, position: 'relative', background: 'white', flexShrink: 0 }}
          className="shadow-2xl overflow-visible select-none"
          onClick={e => { if (e.target === e.currentTarget) { onSelect(null); setEditing(null); } }}
        >
          {fields.map(f => {
            if (!f.show) return null;
            const dpx     = Math.round(f.fontSize * (96 / 72) * SCALE);
            const isEmpty = !f.text || !f.text.trim();
            const isSel   = selected === f.key;
            const isEdit  = editing  === f.key;
            const baseStyle = {
              fontSize:       dpx,
              fontWeight:     f.fontWeight     || 400,
              textDecoration: f.textDecoration || 'none',
              fontFamily:     'Arial,"Arial Narrow",sans-serif',
              whiteSpace:     'nowrap',
              color:          'black',
            };

            return (
              <div key={f.key} style={{ position: 'absolute', left: `${f.x}%`, top: `${f.y}%`, zIndex: isSel ? 10 : 1 }}>
                {isEdit ? (
                  <InlineEditor
                    key={`edit-${f.key}`}
                    text={f.text}
                    onCommit={(text) => commitEdit(f.key, text)}
                    style={{
                      ...baseStyle,
                      outline: '2px solid #2563eb',
                      background: 'rgba(37,99,235,0.07)',
                      borderRadius: 2,
                      padding: '0 2px',
                      cursor: 'text',
                      minWidth: 20,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      ...baseStyle,
                      color:      isEmpty ? '#bbb' : 'black',
                      cursor:     'move',
                      outline:    isSel ? '1.5px solid #2563eb' : 'none',
                      background: isSel ? 'rgba(37,99,235,0.06)' : 'transparent',
                      borderRadius: 2,
                      padding:    '0 1px',
                      ...(f.key === 'pin' && !isEmpty ? { border: '1px solid #555', padding: '0 3px' } : {}),
                    }}
                    onMouseDown={e => startDrag(f.key, e.clientX, e.clientY, e)}
                    onTouchStart={e => startDrag(f.key, e.touches[0].clientX, e.touches[0].clientY, e)}
                    onDoubleClick={e => startEdit(f.key, e)}
                    title="Glisser pour déplacer · Double-clic pour modifier"
                  >
                    {isEmpty ? f.label : f.text}
                  </div>
                )}

                {/* Poignée redimensionnement ↔ */}
                {isSel && !isEdit && (
                  <div
                    style={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, background: '#2563eb', borderRadius: '50%', cursor: 'ew-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
                    onMouseDown={e => { e.stopPropagation(); e.preventDefault(); setResizing({ key: f.key, startX: e.clientX, startFs: f.fontSize }); }}
                    onTouchStart={e => { e.stopPropagation(); setResizing({ key: f.key, startX: e.touches[0].clientX, startFs: f.fontSize }); }}
                    title="Glisser pour redimensionner"
                  >
                    <span style={{ color: 'white', fontSize: 8, userSelect: 'none', lineHeight: 1 }}>↔</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Barre d'outils contextuelle */}
      <CanvasToolbar
        field={selectedField}
        onEdit={() => selectedField && setEditing(selected)}
        onFontSize={d => selectedField && onUpdate(selected, { fontSize: Math.max(5, Math.min(30, selectedField.fontSize + d)) })}
        onToggleVis={() => selectedField && onUpdate(selected, { show: !selectedField.show })}
        onBold={() => selectedField && onUpdate(selected, { fontWeight: (selectedField.fontWeight || 400) >= 700 ? 400 : 900 })}
        onUnderline={() => selectedField && onUpdate(selected, { textDecoration: selectedField.textDecoration === 'underline' ? 'none' : 'underline' })}
      />

      <p className="text-gray-500 text-[10px] mt-1">{labelWidth}mm × {labelHeight}mm · {Math.round(SCALE * 100)}%</p>
    </div>
  );
}

// ── Modal principal ──────────────────────────────────────────────────────────
export const LabelPreviewModal = ({ type, initial, onClose }) => {
  const [labelCfg, setLabelCfg] = useState(readLabelCfg);
  const [fields,   setFields]   = useState(() => initFields(type, initial));
  const [selected, setSelected] = useState(null);
  const [printing, setPrinting] = useState(false);

  // ── Bibliothèque de modèles ──────────────────────────────────────────────
  const [templates,    setTemplates]    = useState(() => loadTemplates(type));
  const [showTpl,      setShowTpl]      = useState(false);
  const [tplName,      setTplName]      = useState('');
  const [renamingId,   setRenamingId]   = useState(null);
  const [renameVal,    setRenameVal]    = useState('');

  const refreshTemplates = () => setTemplates(loadTemplates(type));

  useEffect(() => {
    const h = () => setLabelCfg(readLabelCfg());
    window.addEventListener('label-cfg-change', h);
    return () => window.removeEventListener('label-cfg-change', h);
  }, []);

  const updateField = useCallback((key, updates) =>
    setFields(prev => prev.map(f => f.key === key ? { ...f, ...updates } : f)), []);

  const moveField = useCallback((key, x, y) =>
    setFields(prev => prev.map(f => f.key === key ? { ...f, x, y } : f)), []);

  const saveDefault = () => {
    const posKey = `phonelab_${type}_field_positions`;
    const positions = {};
    fields.forEach(f => {
      positions[f.key] = {
        x: f.x, y: f.y, fontSize: f.fontSize, show: f.show,
        fontWeight: f.fontWeight, textDecoration: f.textDecoration,
      };
    });
    localStorage.setItem(posKey, JSON.stringify(positions));
    toast.success('Positions & styles sauvegardés ✓');
  };

  // Sauvegarde une clé de config étiquette et déclenche la mise à jour du canvas
  const saveLabelCfgKey = (key, value) => {
    const cfg = JSON.parse(localStorage.getItem('phonelab_label_config') || '{}');
    localStorage.setItem('phonelab_label_config', JSON.stringify({ ...cfg, [key]: value }));
    window.dispatchEvent(new Event('label-cfg-change'));
  };

  const applyPreset = (w, h) => {
    const cfg = JSON.parse(localStorage.getItem('phonelab_label_config') || '{}');
    localStorage.setItem('phonelab_label_config', JSON.stringify({ ...cfg, labelWidth: w, labelHeight: h }));
    window.dispatchEvent(new Event('label-cfg-change'));
  };

  const handlePrint = async () => {
    const html = buildFromFields(fields, labelCfg.labelWidth, labelCfg.labelHeight);
    setPrinting(true);
    try {
      // Auto-sauvegarde du modèle actuel avant impression
      const autoName = (
        fields.find(f => f.key === 'name')?.text ||
        fields.find(f => f.key === 'device')?.text ||
        (type === 'sale' ? 'Étiquette vente' : 'Étiquette réparation')
      ).trim() || 'Modèle';
      persistTemplate(autoName, type, fields, labelCfg.labelWidth, labelCfg.labelHeight);
      refreshTemplates();

      if (labelCfg.useLabelPrinter && labelCfg.labelPrinter && typeof window.qz !== 'undefined') {
        await sendToQZ(labelCfg.labelPrinter, html);
        toast.success('Étiquette imprimée via QZ Tray !');
      } else {
        const w = window.open('', '_blank', 'width=320,height=260,toolbar=0,menubar=0,scrollbars=0');
        if (w) { w.document.write(html); w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 400); }
      }
      onClose();
    } catch (e) { toast.error(`Impression: ${e.message}`); }
    finally { setPrinting(false); }
  };

  // ── Actions modèles ───────────────────────────────────────────────────────
  const handleSaveTpl = () => {
    const name = tplName.trim() || 'Modèle sans titre';
    persistTemplate(name, type, fields, labelCfg.labelWidth, labelCfg.labelHeight);
    refreshTemplates();
    setTplName('');
    toast.success(`Modèle "${name}" sauvegardé ✓`);
  };

  const handleLoadTpl = (tpl) => {
    setFields(tpl.fields);
    applyPreset(tpl.labelWidth, tpl.labelHeight);
    setSelected(null);
    toast.success(`Modèle "${tpl.name}" chargé ✓`);
  };

  const handleDeleteTpl = (id) => {
    removeTemplate(id);
    refreshTemplates();
  };

  const handleRenameCommit = (id) => {
    if (renameVal.trim()) { renameTemplateById(id, renameVal.trim()); refreshTemplates(); }
    setRenamingId(null);
    setRenameVal('');
  };

  const title = type === 'sale' ? 'Étiquette vente produit' : 'Étiquette réparation';
  const currentPreset = SIZE_PRESETS.find(p => p.w === labelCfg.labelWidth && p.h === labelCfg.labelHeight);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl flex flex-col"
        style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 bg-gray-800 rounded-t-2xl shrink-0">
          <span className="text-white font-bold flex items-center gap-2">
            <Tag size={15} className={type === 'sale' ? 'text-amber-400' : 'text-orange-400'} />
            Aperçu avant impression — {title}
          </span>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="flex gap-4 flex-1 min-h-0 overflow-hidden p-4">

          {/* Gauche : canvas */}
          <div className="flex flex-col items-center justify-center bg-gray-950 rounded-xl p-3 shrink-0">
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-2">
              Glisser · Double-clic pour modifier
            </p>
            <DragLabelCanvas
              fields={fields}
              labelWidth={labelCfg.labelWidth}
              labelHeight={labelCfg.labelHeight}
              selected={selected}
              onSelect={setSelected}
              onMove={moveField}
              onUpdate={updateField}
            />
          </div>

          {/* Droite : liste champs */}
          <div className="flex-1 overflow-y-auto space-y-1.5">

            {/* ── Bibliothèque de modèles ──────────────────────────────── */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
              <button onClick={() => setShowTpl(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-700 transition-colors">
                <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-300">
                  <BookOpen size={12} className="text-amber-400" />
                  Modèles sauvegardés
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${templates.length ? 'bg-amber-500/20 text-amber-300' : 'bg-gray-700 text-gray-500'}`}>
                    {templates.length}
                  </span>
                </span>
                {showTpl ? <ChevronDown size={13} className="text-gray-400" /> : <ChevronRight size={13} className="text-gray-400" />}
              </button>

              {showTpl && (
                <div className="border-t border-gray-700 px-2 pt-2 pb-2 space-y-1">
                  {/* Liste des modèles */}
                  <div className="max-h-44 overflow-y-auto space-y-1 pr-0.5">
                    {templates.length === 0 && (
                      <p className="text-gray-500 text-xs text-center py-3 italic">
                        Aucun modèle — imprimez une étiquette pour la sauvegarder automatiquement
                      </p>
                    )}
                    {templates.map(tpl => (
                      <div key={tpl.id}
                        className="flex items-center gap-1.5 bg-gray-750 bg-gray-900/60 border border-gray-600 hover:border-amber-600/50 rounded-lg px-2 py-1.5 group transition-colors">
                        {renamingId === tpl.id ? (
                          <input
                            autoFocus
                            value={renameVal}
                            onChange={e => setRenameVal(e.target.value)}
                            onBlur={() => handleRenameCommit(tpl.id)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenameCommit(tpl.id); if (e.key === 'Escape') { setRenamingId(null); setRenameVal(''); } }}
                            className="flex-1 bg-gray-700 text-white text-xs rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-500 min-w-0"
                          />
                        ) : (
                          <button
                            onClick={() => handleLoadTpl(tpl)}
                            onDoubleClick={() => { setRenamingId(tpl.id); setRenameVal(tpl.name); }}
                            title="Cliquer pour charger · Double-clic pour renommer"
                            className="flex-1 text-left text-xs text-gray-200 hover:text-amber-300 truncate font-medium transition-colors min-w-0">
                            {tpl.name}
                          </button>
                        )}
                        <span className="text-gray-600 text-[10px] flex-shrink-0 tabular-nums">
                          {new Date(tpl.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                        </span>
                        <span className="text-[10px] font-mono text-gray-600 flex-shrink-0">{tpl.labelWidth}×{tpl.labelHeight}</span>
                        <button onClick={() => handleDeleteTpl(tpl.id)} title="Supprimer ce modèle"
                          className="p-0.5 text-gray-600 hover:text-red-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Sauvegarder manuellement avec un nom */}
                  <div className="flex gap-1.5 mt-2 pt-2 border-t border-gray-700">
                    <input
                      value={tplName}
                      onChange={e => setTplName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSaveTpl()}
                      placeholder="Nom du modèle..."
                      className="flex-1 bg-gray-700 border border-gray-600 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder-gray-500 min-w-0"
                    />
                    <button onClick={handleSaveTpl}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold rounded-lg transition-all active:scale-95">
                      <Save size={11} /> Sauver
                    </button>
                  </div>
                </div>
              )}
            </div>

            <p className="text-white font-semibold text-sm">Champs de l'étiquette</p>
            {fields.map(f => (
              <div key={f.key} onClick={() => setSelected(f.key)}
                className={`flex items-center gap-2 p-2 rounded-xl cursor-pointer transition-colors
                  ${selected === f.key ? 'bg-gray-700 border border-blue-600' : 'bg-gray-800 border border-gray-700 hover:border-gray-600'}`}>
                <button onClick={e => { e.stopPropagation(); updateField(f.key, { show: !f.show }); }}
                  className={`flex-shrink-0 ${f.show ? 'text-blue-400' : 'text-gray-600'}`}>
                  {f.show ? <Eye size={12} /> : <EyeOff size={12} />}
                </button>
                <span className="text-gray-500 text-[10px] w-16 flex-shrink-0">{f.label}</span>
                <input
                  value={f.text}
                  onChange={e => updateField(f.key, { text: e.target.value })}
                  onClick={e => { e.stopPropagation(); setSelected(f.key); }}
                  placeholder={f.label}
                  className="flex-1 bg-gray-700 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
                />
                <input
                  type="number" min="5" max="30" value={f.fontSize}
                  onChange={e => updateField(f.key, { fontSize: Math.max(5, Math.min(30, +e.target.value || f.fontSize)) })}
                  onClick={e => e.stopPropagation()}
                  className="w-11 bg-gray-700 text-white text-xs font-mono rounded-lg px-1 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                />
                <span className="text-gray-500 text-[10px]">pt</span>
              </div>
            ))}

            {/* Dimensions + formats prédéfinis */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-3 space-y-2 mt-2">
              <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">Dimensions étiquette</p>

              {/* Presets */}
              <div>
                <label className="text-gray-500 text-[10px] block mb-1">Format prédéfini</label>
                <select
                  value={currentPreset ? `${currentPreset.w}x${currentPreset.h}` : ''}
                  onChange={e => {
                    const p = SIZE_PRESETS.find(p => `${p.w}x${p.h}` === e.target.value);
                    if (p) applyPreset(p.w, p.h);
                  }}
                  className="w-full bg-gray-700 border border-gray-600 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">— Personnalisé —</option>
                  {SIZE_PRESETS.map(p => (
                    <option key={`${p.w}x${p.h}`} value={`${p.w}x${p.h}`}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-500 text-[10px] block mb-1">Largeur (mm)</label>
                  <input type="number" min="25" max="150" value={labelCfg.labelWidth}
                    onChange={e => saveLabelCfgKey('labelWidth', +e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 text-white text-xs font-mono rounded-lg px-2 py-1.5 focus:outline-none" />
                </div>
                <div>
                  <label className="text-gray-500 text-[10px] block mb-1">Hauteur (mm)</label>
                  <input type="number" min="15" max="150" value={labelCfg.labelHeight}
                    onChange={e => saveLabelCfgKey('labelHeight', +e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 text-white text-xs font-mono rounded-lg px-2 py-1.5 focus:outline-none" />
                </div>
              </div>
            </div>

            <button onClick={saveDefault}
              className="w-full flex items-center justify-center gap-2 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 text-sm rounded-xl transition-all">
              <Save size={13} /> Enregistrer positions & styles
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-700 bg-gray-800 rounded-b-2xl shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 text-sm rounded-xl">
            Annuler
          </button>
          <button onClick={handlePrint} disabled={printing} data-testid="confirm-print-label-btn"
            className="ml-auto flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold text-sm rounded-xl transition-all active:scale-95">
            <Printer size={14} /> {printing ? 'Impression...' : 'Imprimer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabelPreviewModal;
